// MUST STAY THE FIRST IMPORT.
//
// This module self-executes: it lifts a founder agent invitation token out of
// the URL fragment into module-scoped memory and immediately rewrites the URL to
// a bare /welcome. ES module side effects run in import order and before any
// statement here, so importing it first is what guarantees the credential is
// gone from the URL before Sentry.init() below, before <Analytics /> mounts, and
// before any other module reads window.location. Moving or reordering this
// import would leak a seven-day bearer token into error and analytics context;
// src/lib/agentInviteBootstrap.test.ts fails if it stops being first.
import "./lib/agentInviteBootstrap";
// SECOND, for the same reason: lifts an organization-admin invitation token out
// of the URL (the #org_invite= fragment, or a legacy ?org_invite= query) into
// module memory and scrubs the URL before Sentry.init() below and before
// <Analytics /> mounts. src/lib/orgInviteBootstrap.test.ts pins this position.
import "./lib/orgInviteBootstrap";
// THIRD, for the same reason: lifts the social-OAuth handoff — a one-time opaque
// state and a provider AUTHORIZATION CODE — out of the #state=…&code=… fragment
// on /auth/social/callback into module memory, and scrubs the URL before
// Sentry.init() below and before <Analytics /> mounts. The authorization code is
// a short-lived bearer credential: whoever exchanges it first gets the token.
// src/lib/socialAuthCallbackBootstrap.test.ts pins this position.
import "./lib/socialAuthCallbackBootstrap";
import { createRoot } from "react-dom/client";
import { ThemeProvider } from "next-themes";
import * as Sentry from "@sentry/react";
import App from "./App.tsx";
import "./index.css";
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_APP_ENV || import.meta.env.MODE,
    sendDefaultPii: false,
    tracesSampleRate: 0,
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications.",
      "Non-Error promise rejection captured",
      "NetworkError",
      "Failed to fetch",
      "Load failed",
      "AbortError",
      // Instagram/Facebook in-app browser injected bridge scripts
      /window\.webkit\.messageHandlers/,
      "messageHandlers",
    ],
    denyUrls: [
      /chrome-extension:\/\//i,
      /moz-extension:\/\//i,
      /safari-extension:\/\//i,
      /^chrome:\/\//i,
    ],
  });
}
createRoot(document.getElementById("root")!).render(
  <Sentry.ErrorBoundary
    fallback={
      <div style={{ padding: "2rem", textAlign: "center" }}>
        Something went wrong.
      </div>
    }
  >
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
      <App />
    </ThemeProvider>
  </Sentry.ErrorBoundary>
);
